import { useState, type FC } from "react";
import IconButton from "../../atoms/buttons/IconButton";
import { theme } from "../../../theme";
import Modal from "../Modal";
import Alert from "../Alert";
import { useMarkInvoiceUploadDocumentAsMAnuallyHandled } from "../../../graphql/hooks/documentHandler/useMarkInvoiceUploadDocumentAsManuallyHandled";
import { useTranslation } from "react-i18next";
import Tooltip from "../Tooltip";
import Body from "../../atoms/typography/Body";
import BodySmall from "../../atoms/typography/BodySmall";
import { HorizontalSpacer } from "../../atoms/Spacers";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import ActionButton from "../../atoms/buttons/ActionButton";
import TextField from "../../atoms/inputs/TextField";
import { useCreateComment } from "../../../graphql/hooks/useCreateComment";
import { CheckmarkIconSmall } from "../../atoms/icons/CheckmarkIcons";

interface Props {
  documentId: string;
  clientIdentifier: string;
}

const MarkAsHandledButton: FC<Props> = ({ documentId, clientIdentifier }) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const onSuccessLeaveComment = (result?: any) => {
    if (result?.createComment?.success === true) {
      setComment("");
    } else {
      setErrorMessage(t("documentHandler.commentCouldNotBeSaved"));
    }
  };
  const onErrorLeaveComment = (e?: any) => {
    setErrorMessage(
      t("documentHandler.commentCouldNotBeSaved" + (e?.toString() || "")),
    );
  };
  const { createComment, error: commentError } = useCreateComment({
    onCompleted: onSuccessLeaveComment,
    onError: onErrorLeaveComment,
    noRefetch: true,
  });

  const onLeaveComment = async () => {
    try {
      createComment({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            content: comment,
            parentId: documentId,
            parentType: "INVOICE_UPLOAD_UNIT_DOCUMENT",
          },
        },
      });
    } catch (error) {
      setErrorMessage(
        t("documentHandler.commentCouldNotBeSaved") + error?.toString(),
      );
    }
  };

  if (commentError) {
    setErrorMessage(
      t("documentHandler.commentCouldNotBeSaved") + commentError?.toString(),
    );
  }

  const onCompletedMarkAsHandled = (result: any) => {
    console.log("Document marked as manually handled:", result);
    if (
      result?.documentHandler?.markInvoiceUploadDocumentAsManuallyHandled
        ?.success === true
    ) {
      setSuccessMessage(t("documentHandler.documentMarkedAsHandled"));
      setErrorMessage("");
    }
  };
  const onErrorMarkAsHAndled = (e?: any) => {
    setErrorMessage(e?.toString());
  };

  const { markDocumentAsManuallyHandled, error, loading } =
    useMarkInvoiceUploadDocumentAsMAnuallyHandled({
      onCompleted: onCompletedMarkAsHandled,
      onError: onErrorMarkAsHAndled,
    });

  const onMarkAsHandled = async () => {
    onLeaveComment();
    try {
      await markDocumentAsManuallyHandled({
        variables: { input: { uploadDocumentId: documentId } },
      });
    } catch (e) {
      console.error("Error marking document as manually handled:", e);
      setErrorMessage(e?.toString() ?? "Error marking document as handled");
    }
  };

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  return (
    <div>
      <Tooltip
        content={
          <>
            <Body color="white">{t("documentHandler.markAsHandled")}</Body>
            <BodySmall color="white">
              {t("documentHandler.markAsHandledDescription")}
            </BodySmall>
          </>
        }
      >
        <IconButton
          icon={<CheckmarkIconSmall color={theme.colors.success} />}
          color={theme.colors.success20}
          onClick={onClick}
          size="medium"
        />
      </Tooltip>
      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          closeIcon
          title={t("documentHandler.markAsHandled")}
        >
          <Body textalign="center">
            {t("documentHandler.markAsHandledDescription")}
          </Body>
          <HorizontalSpacer spacing={2} />
          <TextField
            value={comment}
            onChange={setComment}
            label={t("common.comment")}
            fullWidth
          />
          <HorizontalSpacer spacing={2} />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "2rem",
              alignItems: "center",
            }}
          >
            <DefaultButton
              title={t("common.cancel")}
              onClick={() => setModalOpen(false)}
            />
            <ActionButton
              title={t("documentHandler.markAsHandled")}
              onClick={onMarkAsHandled}
              loading={loading}
              color={theme.colors.primary}
              disabled={!comment}
            />
          </div>

          {(errorMessage || error) && (
            <Alert
              type="error"
              title={t("documentHandler.couldNotBeMarkedAsHandled")}
              message={errorMessage || error?.toString()}
            />
          )}
          {successMessage && <Alert type="success" title={successMessage} />}
        </Modal>
      )}
    </div>
  );
};

export default MarkAsHandledButton;
